import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ArrowLeft, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useSEO } from '@/hooks/useSEO';
import PricingTiersBlock from '@/components/pricing/PricingTiersBlock';
import { trackEvent } from '@/lib/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Statische Default-Pläne: sofort im HTML gerendert (SEO/Snapshot),
// werden nach dem Laden durch die API-Antwort ersetzt.
const DEFAULT_PLANS = {
  free: { name: 'free', price: 0, interval: null, features: { customers: 1, projects: 1, automatic_invoices: false, dashboard: false, tax_overview: false } },
  basic: {
    monthly: { name: 'basic_monthly', price: 9, interval: 'month', features: { customers: 2, projects: 4, automatic_invoices: true, dashboard: true, tax_overview: false } },
    yearly: { name: 'basic_yearly', price: 90, interval: 'year', features: { customers: 2, projects: 4, automatic_invoices: true, dashboard: true, tax_overview: false } },
  },
  pro: {
    monthly: { name: 'pro_monthly', price: 19, interval: 'month', features: { customers: 'unlimited', projects: 'unlimited', automatic_invoices: true, dashboard: true, tax_overview: true } },
    yearly: { name: 'pro_yearly', price: 190, interval: 'year', features: { customers: 'unlimited', projects: 'unlimited', automatic_invoices: true, dashboard: true, tax_overview: true } },
  },
};

function Pricing() {
  const { t } = useTranslation();
  const { user, isAuthenticated, authToken } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [billingInterval, setBillingInterval] = useState('monthly');
  const [plans, setPlans] = useState(DEFAULT_PLANS);
  const [lifetimeBundleInfo, setLifetimeBundleInfo] = useState(null);
  const [trialEligibility, setTrialEligibility] = useState(null);

  useSEO({
    title: 'Preise & Pläne – ivoliq Freelancer-Buchhaltung | Ab 0€/Monat',
    description: 'ivoliq Preise: Free, Basic und Pro Pläne für Freelancer. Zeiterfassung, Rechnungen, Belegerfassung und Steuerübersicht. Kostenlos starten, jederzeit upgraden.',
    canonical: 'https://ivoliq.io/pricing',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [plansResponse, lifetimeResponse] = await Promise.all([
          axios.get(`${API}/plans`),
          axios.get(`${API}/lifetime-bundle-info`)
        ]);
        setPlans(plansResponse.data);
        setLifetimeBundleInfo(lifetimeResponse.data);
        
        // Fetch trial eligibility if user is authenticated
        if (isAuthenticated && authToken) {
          try {
            const trialResponse = await axios.get(`${API}/subscriptions/trial-eligibility`, {
              headers: { Authorization: `Bearer ${authToken}` }
            });
            setTrialEligibility(trialResponse.data);
          } catch (error) {
            console.error('Failed to fetch trial eligibility:', error);
          }
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
        // Kein Toast: statische DEFAULT_PLANS sind bereits gerendert
      }
    };
    fetchData();
  }, [isAuthenticated, authToken]);

  const handleUpgrade = async (planId) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (user?.plan === planId) {
      return;
    }

    if (planId === 'free') {
      navigate('/settings');
      toast.info(t('pricing.downgradeFree'));
      return;
    }

    // Automatically apply trial if eligible and this is first paid plan
    const shouldUseTrial = trialEligibility?.eligible && 
                          user?.plan === 'free' && 
                          (planId === 'basic_monthly' || planId === 'basic_yearly' || planId === 'pro_monthly' || planId === 'pro_yearly');

    setLoading(planId);
    try {
      // Conversion-Funnel (PostHog, August 2026)
      trackEvent('checkout_started', { plan: planId, trial: Boolean(shouldUseTrial) });
      const response = await axios.post(
        `${API}/subscriptions/create`,
        {
          plan: planId,
          origin_url: window.location.origin,
          upgrade_timing: 'immediate',
          use_trial: shouldUseTrial
        },
        {
          headers: { Authorization: `Bearer ${authToken}` }
        }
      );

      window.location.href = response.data.url;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || t('pricing.upgradeFailed');
      toast.error(errorMessage);
      setLoading(null);
    }
  };

  const handleLifetimePurchase = async () => {
    if (!isAuthenticated) {
      navigate('/login?intent=lifetime');
      return;
    }

    if (user?.plan === 'lifetime') {
      toast.info('Du hast bereits Lifetime Zugang!');
      return;
    }

    setLoading('lifetime');
    try {
      console.log('🚀 Starting Lifetime Bundle checkout...');
      trackEvent('checkout_started', { plan: 'lifetime' });
      const response = await axios.post(
        `${API}/lifetime-bundle/checkout`,
        { 
          plan: 'lifetime',
          origin_url: window.location.origin,
          upgrade_timing: 'immediate'
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      console.log('✅ Checkout response:', response.data);

      if (response.data.checkout_url) {
        console.log('🔗 Redirecting to:', response.data.checkout_url);
        window.location.href = response.data.checkout_url;
      } else {
        console.error('❌ No checkout_url in response:', response.data);
        toast.error('Checkout-URL fehlt. Bitte versuchen Sie es später erneut.');
        setLoading(null);
      }
    } catch (error) {
      console.error('❌ Lifetime checkout error:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Fehler beim Laden des Checkouts');
      setLoading(null);
    }
  };

  const getLifetimePrice = () => {
    if (!lifetimeBundleInfo) return '199';
    const sold = lifetimeBundleInfo.sold_count || 0;
    if (sold < 50) return '199';
    if (sold < 150) return '249';
    return null;
  };

  const getLifetimeBadge = () => {
    if (!lifetimeBundleInfo) return '';
    const sold = lifetimeBundleInfo.sold_count || 0;
    if (sold < 50) return `Nur noch ${50 - sold} verfügbar`;
    if (sold < 150) return `Nur noch ${150 - sold} verfügbar`;
    return 'Ausverkauft';
  };

  const isLifetimeSoldOut = () => {
    if (!lifetimeBundleInfo) return false;
    return (lifetimeBundleInfo.sold_count || 0) >= 150;
  };

  if (!plans) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  const getPlanDisplay = (tier) => {
    if (tier === 'free') return plans.free;
    return billingInterval === 'monthly' ? plans[tier].monthly : plans[tier].yearly;
  };

  const isCurrentPlan = (planId) => {
    return user?.plan === planId;
  };

  const getButtonText = (planId) => {
    if (!isAuthenticated) return t('pricing.getStarted');
    if (planId === 'free') {
      return isCurrentPlan('free') ? t('pricing.currentPlan') : t('pricing.downgrade');
    }
    if (isCurrentPlan(planId)) return t('pricing.currentPlan');
    
    const currentTier = user?.plan?.includes('basic') ? 'basic' : user?.plan?.includes('pro') ? 'pro' : 'free';
    const targetTier = planId.includes('basic') ? 'basic' : 'pro';
    
    if (currentTier === 'free') return t('pricing.upgrade');
    if (currentTier === 'basic' && targetTier === 'pro') return t('pricing.upgrade');
    if (currentTier === 'pro' && targetTier === 'basic') return t('pricing.downgrade');
    
    return t('pricing.changePlan');
  };

  // Define what each plan includes - CLEAR and NO CONFUSION
  const planFeatures = {
    free: {
      capacity: { customers: 1, projects: 1 },
      included: [
        'Zeiterfassung mit Timer',
        'Ausgaben dokumentieren',
        'Belegerkennung per OCR',
        'PDF-Rechnungen erstellen'
      ],
      notIncluded: [
        'Belegspeicherung',
        'Angebote erstellen',
        'Automatische Rechnungserstellung',
        'KI-Belegerkennung',
        'Beleg-Eingang per E-Mail',
        'Dashboard & Analytics',
        'Steuerberechnung'
      ]
    },
    basic: {
      capacity: { customers: 2, projects: 4 },
      included: [
        'Zeiterfassung mit Timer',
        'Ausgaben dokumentieren',
        'Belegerkennung per OCR',
        'Belegspeicherung (PDF/Foto)',
        'Duplikat-Erkennung für Belege',
        'PDF-Rechnungen erstellen',
        'Automatische Rechnungen aus Zeiteinträgen',
        'Angebote mit Online-Annahme',
        'Dashboard mit Umsatzübersicht',
        'Finanz-Auswertungen'
      ],
      notIncluded: [
        'KI-Belegerkennung',
        'Beleg-Eingang per E-Mail',
        'Belegexport für Steuerberater',
        'Steuerberechnung (DE/AT/CH)'
      ]
    },
    pro: {
      capacity: { customers: '∞', projects: '∞' },
      included: [
        'Zeiterfassung mit Timer',
        'Ausgaben dokumentieren',
        'KI-Belegerkennung (Anthropic)',
        'Beleg-Eingang per E-Mail (persönliche Adresse)',
        'Belegspeicherung (PDF/Foto)',
        'Duplikat-Erkennung für Belege',
        'Belegexport für Steuerberater (ZIP)',
        'PDF-Rechnungen erstellen',
        'Automatische Rechnungen aus Zeiteinträgen',
        'Angebote mit Online-Annahme',
        'Dashboard mit Umsatzübersicht',
        'Finanz-Auswertungen',
        'Steuerberechnung (DE/AT/CH)',
        'Einkommensteuer, USt, Sozialabgaben'
      ],
      notIncluded: []
    }
  };

  const renderPlanCard = (tier, isPopular = false) => {
    const plan = getPlanDisplay(tier);
    const planId = plan.name;
    const features = planFeatures[tier];
    
    return (
      <div
        key={planId}
        className={`relative bg-white rounded-2xl shadow-xl p-8 flex flex-col ${
          isPopular ? 'ring-2 ring-emerald-500 transform scale-105' : ''
        }`}
      >
        {isPopular && (
          <div className="absolute -top-3.5 left-0 right-0 flex justify-center">
            <span className="bg-emerald-500 text-white px-5 py-1 text-sm font-semibold rounded-full shadow-md">
              {t('pricing.popular')}
            </span>
          </div>
        )}

        {/* Plan Header */}
        <div className="text-center mb-6">
          <h3 className="text-3xl font-bold text-gray-900 mb-2">
            {t(`pricing.plans.${tier}.name`)}
          </h3>
          <p className="text-gray-600 mb-4">
            {t(`pricing.plans.${tier}.description`)}
          </p>
          <div className="mb-4">
            <span className="text-5xl font-bold text-gray-900">
              €{plan.price}
            </span>
            {plan.interval && (
              <>
                <span className="text-gray-600 ml-2">
                  / {plan.interval === 'month' ? t('pricing.perMonth') : t('pricing.perYear')}
                </span>
                <p className="text-xs text-gray-500 mt-2">zzgl. MwSt.</p>
              </>
            )}
            {!plan.interval && (
              <span className="text-gray-600 ml-2 block text-sm mt-1">Kostenlos für immer</span>
            )}
          </div>
        </div>

        {/* Capacity */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <p className="text-sm font-bold text-gray-800 mb-2">📊 Kapazität:</p>
          <div className="text-sm text-gray-700">
            <span className="font-semibold">{features.capacity.customers}</span> {features.capacity.customers === 1 ? 'Kunde' : 'Kunden'} • {' '}
            <span className="font-semibold">{features.capacity.projects}</span> {features.capacity.projects === 1 ? 'Projekt' : 'Projekte'}
          </div>
        </div>

        {/* Included Features */}
        <div className="flex-1 mb-6">
          <p className="text-sm font-bold text-gray-800 mb-3">✓ Enthalten:</p>
          <ul className="space-y-2">
            {features.included.map((feature, index) => (
              <li key={index} className="flex items-start gap-2">
                <Check className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700">{feature}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Not Included */}
        {features.notIncluded.length > 0 && (
          <div className="mb-6">
            <p className="text-sm font-bold text-gray-600 mb-3">✗ Nicht enthalten:</p>
            <ul className="space-y-2">
              {features.notIncluded.map((feature, index) => (
                <li key={index} className="flex items-start gap-2">
                  <X className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-500">{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* CTA Button */}
        <div className="space-y-2">
          {/* Main CTA Button */}
          <button
            onClick={() => handleUpgrade(planId)}
            disabled={isCurrentPlan(planId) || loading === planId}
            className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors ${
              isCurrentPlan(planId)
                ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                : isPopular
                ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                : 'bg-gray-900 text-white hover:bg-gray-800'
            }`}
          >
            {loading === planId ? (
              <span className="flex items-center justify-center gap-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                {t('common.loading')}
              </span>
            ) : (
              getButtonText(planId)
            )}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Soft emerald-tinted backdrop */}
      <div className="absolute inset-0 bg-gradient-to-b from-emerald-50/40 via-white to-white pointer-events-none" />

      <div className="relative container mx-auto px-4 py-10 md:py-16 max-w-7xl">
        {/* Header with Back Button */}
        {isAuthenticated && (
          <div className="mb-8">
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
              data-testid="pricing-back-link"
            >
              <ArrowLeft className="h-4 w-4" />
              Zurück zum Dashboard
            </Link>
          </div>
        )}

        {/* Hero */}
        <div className="text-center mb-14 md:mb-16 max-w-2xl mx-auto">
          <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-700 px-3 py-1 text-xs font-bold uppercase tracking-widest mb-5">
            Preise & Pakete
          </span>
          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-zinc-900 mb-4"
            style={{ fontFamily: 'Manrope, sans-serif' }}
            data-testid="pricing-hero-title"
          >
            Wähle dein ivoliq.
          </h1>
          <p className="text-lg md:text-xl text-zinc-600 leading-relaxed">
            Kostenlos starten. Wachsen, wenn du es brauchst. Keine Vertragsbindung.
          </p>
        </div>

        {/* Tier Block */}
        <PricingTiersBlock
          plans={plans}
          billingInterval={billingInterval}
          onBillingChange={setBillingInterval}
          onSelectPlan={(planId) => handleUpgrade(planId)}
          onLifetime={!isLifetimeSoldOut() && lifetimeBundleInfo && getLifetimePrice() ? handleLifetimePurchase : null}
          currentPlan={user?.plan}
          loadingPlanId={loading}
          showLifetime={!isLifetimeSoldOut() && !!lifetimeBundleInfo && !!getLifetimePrice()}
          lifetimePrice={getLifetimePrice()}
          lifetimeBadge={getLifetimeBadge()}
          lifetimeSoldOut={isLifetimeSoldOut()}
        />

        {/* Trust-Footer */}
        <div className="max-w-3xl mx-auto mt-16 md:mt-20 text-center">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
            {[
              { label: 'Sichere Zahlung', sub: 'via Stripe' },
              { label: 'Jederzeit kündbar', sub: 'monatlich' },
              { label: 'DSGVO-konform', sub: 'Server in DE' },
              { label: 'DACH-Support', sub: 'auf Deutsch' },
            ].map((t) => (
              <div key={t.label} className="text-center">
                <div className="text-sm font-bold text-zinc-900">{t.label}</div>
                <div className="text-xs text-zinc-500">{t.sub}</div>
              </div>
            ))}
          </div>
          <p className="text-xs text-zinc-400">
            Alle Preise verstehen sich zzgl. der gesetzlichen Mehrwertsteuer (DE/AT 19%/20%, CH-Mandanten netto).
          </p>
        </div>
      </div>
    </div>
  );
}

export default Pricing;
